/**
 * WorkspaceApplyEntryCard.jsx — RC 1.49.7 Slice 6 (operator P0, 2026-02)
 *
 * Inline, in-flow, NON-FLOATING entry surface for the route-based
 * Application Workspace. Renders on `/jobs/:id` whenever the user
 * clicks Apply AND `isWorkspaceModeEnabledForUser(user)` is true.
 * Pre-Slice-6 the gate at `JobDetailPage.requestApply` jumped
 * straight to `/applications/workspace/:id` — bypassing the
 * polished entry-notice UX that wizard users still see (RC 1.49.x
 * P0.4 `WizardEntryNotice`).
 *
 * Hard rails (operator-locked):
 *   • Frontend only. No backend. No schema. No pricing/Stripe.
 *   • Inline card — NOT a Dialog/Sheet/Drawer/Popover/Modal/Toast.
 *     The workspace ethos is no-floating-UI, and this entry card
 *     extends that to the entry-point page.
 *   • Self-contained state: a single `expanded` boolean held by the
 *     caller (JobDetailPage). No localStorage, no `wizardState.js`,
 *     no marker, no resume semantics — the workspace re-entry rule
 *     is "URL is the state".
 *   • Costs render directly from `useCredits().usageCosts`. Zero
 *     billing math on the client. If a price is missing, the line
 *     surfaces a `…` placeholder rather than crashing.
 *   • Free-fit footnote explicitly cites Slice 5c: the daily free
 *     allowance applies to Source-CV exploration only; variant
 *     Analyse Fit always charges Credits.
 *   • Cancel collapses inline; "Open workspace" navigates to
 *     `/applications/workspace/:jobId`. No history pollution.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCredits } from '@/lib/creditsContext';
import {
  useWorkspaceAcknowledgement,
  WorkspaceAcknowledgementDialog,
} from '@/components/workspace/WorkspaceAcknowledgement';

/**
 * Format a Credits cost line. Returns `…` when the price is null /
 * undefined / not yet loaded. Returns `Included` when the price is
 * exactly 0 (e.g. application_pack on Base).
 */
function fmtCost(cost) {
  if (cost == null) return '…';
  const n = Number(cost);
  if (!Number.isFinite(n)) return '…';
  if (n === 0) return 'Included with your plan';
  return `${n} credit${n === 1 ? '' : 's'}`;
}

export default function WorkspaceApplyEntryCard({
  jobId,
  onCancel,
  rootTestId = 'apply-entry-card',
}) {
  const navigate = useNavigate();
  const { usageCosts } = useCredits();
  // RC 1.58 Phase 4 (operator-locked, 2026-02-14) — one-time
  // acknowledgement modal between this entry card and the workspace
  // route. `guardedNavigate` is a drop-in replacement for navigate()
  // that defers the route push until the user confirms in the modal
  // (or routes immediately when the session has already acknowledged).
  const { guardedNavigate, dialogProps } = useWorkspaceAcknowledgement();

  const handleConfirm = () => {
    // Workspace defaults to `?section=cv` on landing — explicit
    // param is redundant. We keep the URL clean.
    guardedNavigate(`/applications/workspace/${jobId}`);
  };

  return (
    <section
      data-testid={rootTestId}
      className="rounded-xl border border-ndor-line bg-ndor-surface-alt p-5 sm:p-6 space-y-5"
      aria-labelledby="apply-entry-card-heading"
    >
      <header className="space-y-1.5">
        <p
          className="text-[11px] uppercase tracking-[0.16em] text-ndor-ink-muted font-medium"
          data-testid="apply-entry-eyebrow"
        >
          Application workspace
        </p>
        <h2
          id="apply-entry-card-heading"
          className="text-lg font-semibold text-ndor-ink"
        >
          What to expect
        </h2>
      </header>

      <p
        className="text-sm text-ndor-ink-muted leading-relaxed"
        data-testid="apply-entry-intro"
      >
        The workspace is a live session for tailoring your CV, drafting a
        cover letter, preparing for interview, and assembling an
        Application Pack. Each step is optional and runs only when{' '}
        <em>you</em> click.
      </p>

      <ol
        className="space-y-2 text-sm text-ndor-ink"
        data-testid="apply-entry-steps"
      >
        {/* RC 1.56 P3 (operator-locked, 2026-05-13 PM) — Mobile
            pricing-row stacking. Pre-RC1.56 each row used
            `flex items-baseline justify-between gap-3` which
            produced a two-column "broken table" layout on narrow
            screens: the long descriptive copy wrapped to multiple
            lines while the right-aligned price floated detached.
            On <sm we now stack title/desc above the price; on sm+
            the original two-column layout is preserved. */}
        <li
          className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
          data-testid="apply-entry-cost-cv-improve"
        >
          <span>
            <span className="font-medium">1. Tailor your CV</span>
            <span className="text-ndor-ink-muted">
              {' '}— rewrite your CV (also called a Resume) for this job
            </span>
          </span>
          <span className="font-mono-ui tabular-nums text-ndor-ink-muted">
            {fmtCost(usageCosts?.cv_improve)}
          </span>
        </li>
        <li
          className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
          data-testid="apply-entry-cost-cover-letter"
        >
          <span>
            <span className="font-medium">2. Cover letter</span>
            <span className="text-ndor-ink-muted">
              {' '}— drafted to your tone
            </span>
          </span>
          <span className="font-mono-ui tabular-nums text-ndor-ink-muted">
            {fmtCost(usageCosts?.cover_letter)}
          </span>
        </li>
        <li
          className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
          data-testid="apply-entry-cost-interview-prep"
        >
          <span>
            <span className="font-medium">3. Interview Prep</span>
            <span className="text-ndor-ink-muted">
              {' '}— 5-bucket interview questions &amp; guidance
            </span>
          </span>
          <span className="font-mono-ui tabular-nums text-ndor-ink-muted">
            {fmtCost(usageCosts?.interview_prep)}
          </span>
        </li>
        <li
          className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
          data-testid="apply-entry-cost-application-pack"
        >
          <span>
            <span className="font-medium">4. Application Pack</span>
            <span className="text-ndor-ink-muted">
              {' '}— the bundle you submit
            </span>
          </span>
          <span className="font-mono-ui tabular-nums text-ndor-ink-muted">
            {fmtCost(usageCosts?.application_pack)}
          </span>
        </li>
      </ol>

      <div
        className="rounded-md border border-ndor-line bg-ndor-surface px-4 py-3 space-y-1.5 text-[13px] text-ndor-ink-muted leading-relaxed"
        data-testid="apply-entry-rules"
      >
        <p>
          <span className="text-ndor-ink font-medium">Steps you don&rsquo;t run are not charged.</span>
          {' '}Each completed document is saved to your{' '}
          <span className="text-ndor-ink font-medium">Documents</span>{' '}library and stays available there.
        </p>
        <p data-testid="apply-entry-session-note">
          The workspace itself is a live session. If you leave before
          assembling the Application Pack, you can start a new
          session later — your completed CVs, cover letters and
          interview prep notes remain in Documents and an Application
          Pack can be reassembled from them.
        </p>
        <p data-testid="apply-entry-cv-charge-waiver-note">
          If your tailored CV does not improve on your Source CV&rsquo;s
          fit score, the CV tailoring charge is{' '}
          <span className="text-ndor-ink font-medium">waived automatically</span>
          {' '}— you only pay for a genuine uplift over your Source CV
          baseline.
        </p>
      </div>

      {/* RC 1.56 P3 — Mobile-friendly footer. On <sm the buttons
          stack vertically with "Open workspace" on top (primary
          action above secondary). On sm+ the original
          right-aligned row is preserved. `w-full sm:w-auto` makes
          each button fill the row on mobile so tap targets are
          big and visually intentional, mirroring the Applications
          page card pattern. */}
      <footer className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          data-testid="apply-entry-cancel-button"
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={handleConfirm}
          data-testid="apply-entry-confirm-button"
          className="w-full sm:w-auto"
        >
          Open workspace
        </Button>
      </footer>
      {/* RC 1.58 Phase 4 — one-time acknowledgement modal. Renders
          only when the user clicks the confirm button above AND
          hasn't acknowledged in this browser session yet. UI-only:
          no orchestration / billing / session-guard impact. */}
      <WorkspaceAcknowledgementDialog {...dialogProps} />
    </section>
  );
}
